// Manual CoC workbook helpers (CHA-630). ONE reader for the ChainTo Manual CoC template, shared by the upload
// screen (CpscManualCoc) and the Amend products dialog (CpscDrafts), so the two can never read the same file
// differently (§8.2). Pure apart from XLSX; no React / Firebase deps.
import * as XLSX from 'xlsx';

const s = (v) => (v == null ? '' : String(v));

// The first sheet as a grid, plus where it sits on the sheet: sheet_to_json starts at the sheet's used range,
// which need not be A1, so grid[0] is sheet row `r0` and grid column 0 is sheet column `c0` (both 0-based).
function readGrid(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, blankrows: true });
  const range = ws && ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : { s: { r: 0, c: 0 } };
  // any Report No alias saveManualCoc accepts — not just the exact 'Report No' (CodeRabbit + L3, #488)
  const headerIdx = grid.findIndex((r) => (r || []).some((c) => ALIASES.reportNo.includes(s(c).trim().toLowerCase().replace(/\s+/g, ' '))));
  return { ws, grid, headerIdx, r0: range.s.r, c0: range.s.c };
}

/**
 * readManualCocRecords(arrayBuffer) → { records, rowNumbers } | { error }. First sheet, header row located by
 * a "Report No" cell, blank rows and the template's REQUIRED/optional marker row dropped (CHA-657), every cell
 * stringified before the records go to saveManualCoc. CHA-544 — `rowNumbers[i]` is the 1-based sheet row
 * record i came from, so the pre-flight can point at a cell and the corrected copy can patch it.
 */
export function readManualCocRecords(buf) {
  const { grid, headerIdx, r0 } = readGrid(XLSX.read(buf, { type: 'array' }));
  if (headerIdx < 0) return { error: 'Not a ChainTo Manual CoC template — no "Report No" header found.' };
  const headers = (grid[headerIdx] || []).map((h) => s(h).trim());
  const kept = grid.map((r, idx) => ({ r, idx })).slice(headerIdx + 1)
    .filter(({ r }) => (r || []).some((c) => s(c).trim()))
    // CHA-657 — the template's second row marks columns REQUIRED / optional; it is not a report.
    .filter(({ r }) => !(r || []).filter((c) => s(c).trim()).every((c) => /^(required|optional)$/i.test(s(c).trim())));
  const records = kept.map(({ r }) => Object.fromEntries(headers.map((h, i) => [h, r[i] == null ? '' : String(r[i])])));
  return { records, rowNumbers: kept.map(({ idx }) => r0 + idx + 1) };
}

/**
 * correctedWorkbook(arrayBuffer, fixes) → { data, applied, skipped }. CHA-544 — the operator's OWN workbook with
 * only the ticked cells changed: `fixes` is [{ row, header, willBe }] (row = 1-based sheet row, header = the
 * operator's header text as the pre-flight reported it). Every other cell, column and sheet is left as it was.
 * A fix whose header is not on the sheet, or whose row is above the header, is skipped (and counted) rather
 * than written somewhere else. `data` is an xlsx ArrayBuffer for download.
 */
export function correctedWorkbook(buf, fixes) {
  const wb = XLSX.read(buf, { type: 'array' });
  const { ws, grid, headerIdx, r0, c0 } = readGrid(wb);
  if (headerIdx < 0) return { error: 'Not a ChainTo Manual CoC template — no "Report No" header found.' };
  const headerRow = r0 + headerIdx + 1; // 1-based
  const colOf = new Map((grid[headerIdx] || []).map((h, i) => [s(h).trim(), c0 + i]));
  let applied = 0; let skipped = 0;
  for (const f of fixes || []) {
    const c = colOf.get(s(f.header).trim());
    if (c == null || !Number.isInteger(f.row) || f.row <= headerRow || f.willBe == null) { skipped += 1; continue; }
    // a plain text cell: a corrected date must not be re-read by Excel as a serial or a US/EU date
    ws[XLSX.utils.encode_cell({ r: f.row - 1, c })] = { t: 's', v: String(f.willBe) };
    applied += 1;
  }
  return { data: XLSX.write(wb, { type: 'array', bookType: 'xlsx' }), applied, skipped };
}

// Header aliases for the three fields the amend flow reads. Mirrors FIELD_ALIASES in
// functions/cpscManualCoc.js — the server cannot be imported here, so keep the two in step.
const ALIASES = {
  reportNo: ['report no', 'report no.', 'report number'],
  productId: ['product id', 'product ids', 'product id(s)'],
  styleNo: ['style no', 'style no.', 'style number'],
};
const norm = (h) => s(h).trim().toLowerCase().replace(/\s+/g, ' ');
function pick(rec, field) {
  const lower = {};
  for (const [k, v] of Object.entries(rec || {})) lower[norm(k)] = v;
  for (const a of ALIASES[field]) if (s(lower[a]).trim()) return s(lower[a]).trim();
  return '';
}

/**
 * productsForReport(records, reportNo) → { productIds, otherReports }. The product ids a workbook names for
 * ONE report, read the way saveManualCoc reads them: Product IDs from every row of the report, split on , ; /
 * — and ONLY if the report names no Product ID at all, derived from its Style Nos split on whitespace , ; /
 * (CHA-306; report-level, not row-level, exactly as the server does). Rows for other reports are counted, not
 * used: amending several reports from one workbook is deferred (CHA-630 ruling 2).
 */
export function productsForReport(records, reportNo) {
  const want = s(reportNo).trim();
  const productIds = [];
  const styleNos = [];
  const otherReports = new Set();
  for (const rec of records || []) {
    const rn = pick(rec, 'reportNo');
    if (!rn) continue;
    if (rn !== want) { otherReports.add(rn); continue; }
    for (const p of pick(rec, 'productId').split(/[,;/]+/).map((x) => x.trim()).filter(Boolean)) if (!productIds.includes(p)) productIds.push(p);
    for (const st of pick(rec, 'styleNo').split(/[,;]+/).map((x) => x.trim()).filter(Boolean)) if (!styleNos.includes(st)) styleNos.push(st);
  }
  if (!productIds.length) {
    for (const p of styleNos.flatMap((st) => st.split(/[\s,;/]+/)).map((x) => x.trim()).filter(Boolean)) if (!productIds.includes(p)) productIds.push(p);
  }
  return { productIds, otherReports: [...otherReports] };
}

/** splitTypedIds("A-1, B-2\nC-3") → ['A-1','B-2','C-3'] — typed additions, one id per comma/semicolon/line. */
export function splitTypedIds(text) {
  const out = [];
  for (const p of s(text).split(/[,;\n\r\t]+/).map((x) => x.trim()).filter(Boolean)) if (!out.includes(p)) out.push(p);
  return out;
}

/**
 * amendDiff(current, additions, removals) → { added, removed, unchanged, finalList }. `finalList` keeps the
 * certified order, then appends genuinely new ids — so the certificate's primary product never moves.
 * A removal only counts if the id is actually on the current list.
 */
export function amendDiff(current, additions, removals) {
  const cur = [...new Set((current || []).map((p) => s(p).trim()).filter(Boolean))];
  const rem = new Set((removals || []).filter((p) => cur.includes(p)));
  const added = [...new Set((additions || []).map((p) => s(p).trim()).filter(Boolean))].filter((p) => !cur.includes(p));
  const unchanged = cur.filter((p) => !rem.has(p));
  return { added, removed: cur.filter((p) => rem.has(p)), unchanged, finalList: [...unchanged, ...added] };
}
